import React, { useMemo, useRef, useState } from "react";
import useTranslation from "../../shared/hooks/useTranslation";
import { dt } from "../../shared/themes/redesignTokens";
import { emailRegex } from "../../shared/validators";
import { landingOriginForCurrentHost, landingOriginForSurface } from "../../shared/utils/hostPolicy";

/**
 * The Book-a-Demo form on the `.com` invite-only page (HOST_POLICY_SPEC §6 FE-B).
 *
 * Posts a `demo` lead straight to the standalone LeadCapture service on the LANDING origin —
 * deliberately NOT through the app's axios instance:
 *   - the app instance attaches an `Authorization` header, which LeadCapture's CORS policy does not
 *     allow (`Content-Type` only, no credentials) — the preflight would fail;
 *   - the URL must be the ABSOLUTE landing origin, because LeadCapture is not mounted under the app
 *     site and a relative path would be answered by the SPA catch-all with `index.html` and HTTP 200
 *     (a false success — see `shared/utils/hostPolicy.ts`).
 *
 * SECURITY (SEC-REQ-HP-10): `credentials: "omit"`, and the payload carries ONLY the fields the user
 * typed plus the two anti-bot fields. NO query/route parameter is ever forwarded — in particular
 * `?partnerInvite=` must never reach the lead body.
 *
 * Every error string is a STATIC i18n string; user input is never echoed back into the UI.
 */
const LEAD_ENDPOINT_PATH = "/leadcapture/v1/leads";

/** Field caps mirror the server-side LeadValidator — the form should not let a 400 happen. */
const MAX = {
  name: 100,
  email: 256,
  company: 200,
  phone: 30,
  message: 2000,
} as const;

type Status = "idle" | "submitting" | "success";
type ErrorKey = "errorGeneric" | "errorRateLimited" | "errorNetwork";

interface BookDemoFormProps {
  /** Rendered instead of the form once a lead has been accepted. */
  onSuccess?: () => void;
  /**
   * Set when the SERVER refused registration on this host (403
   * `REGISTRATION_NOT_AVAILABLE_ON_HOST`) — i.e. the server classified this hostname as `.com`.
   *
   * The server's word beats the client's hard-coded `COM_APP_HOSTS` list (Codex C-2): on a `.com`
   * app host the client list does not know about, the hostname check would send the lead to the
   * `.cc` landing origin — the wrong funnel, and a wrong `SourceSite`. When this is absent (the
   * route-level invite-only page, which has no refusal to go on) the client classification is the
   * fallback and today's behaviour is unchanged.
   */
  refusedSurface?: "com";
}

const BookDemoForm = ({ onSuccess, refusedSurface }: BookDemoFormProps) => {
  const { translate, getCurrentLanguage } = useTranslation("account");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  /** Honeypot — must stay empty. A bot that fills every input trips it server-side. */
  const [website, setWebsite] = useState("");

  const [status, setStatus] = useState<Status>("idle");
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  /**
   * Captured at MOUNT, not at submit: LeadCapture rejects a submission that arrives less than
   * three seconds after the form was rendered (min-time-on-form bot gate).
   */
  const mountedAt = useRef(Date.now());

  const emailValid = useMemo(() => emailRegex.test(email.trim()), [email]);
  const canSubmit =
    name.trim().length > 0 && emailValid && company.trim().length > 0 && status !== "submitting";

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setShowValidation(true);

    if (!canSubmit) {
      return;
    }

    setStatus("submitting");
    setErrorKey(null);

    const payload = {
      formType: "demo",
      name: name.trim(),
      email: email.trim(),
      company: company.trim(),
      phone: phone.trim(),
      message: message.trim(),
      website,
      clientTimestamp: mountedAt.current,
      sourceLanguage: getCurrentLanguage(),
    };

    try {
      const leadOrigin =
        refusedSurface === "com" ? landingOriginForSurface("com") : landingOriginForCurrentHost();

      const response = await fetch(`${leadOrigin}${LEAD_ENDPOINT_PATH}`, {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        setStatus("idle");
        setErrorKey("errorRateLimited");
        return;
      }

      // A 200 is NOT enough. If this ever gets pointed at a path the SPA catch-all serves, the
      // body is `index.html` and `response.json()` throws — which is exactly the false success we
      // must not report. Success requires status 200 AND a JSON body with `ok === true`.
      const accepted = response.status === 200 && (await readOk(response));
      if (!accepted) {
        setStatus("idle");
        setErrorKey("errorGeneric");
        return;
      }

      setStatus("success");
      onSuccess?.();
    } catch {
      // Network failure, DNS, CORS preflight rejection, aborted request.
      setStatus("idle");
      setErrorKey("errorNetwork");
    }
  };

  if (status === "success") {
    return (
      <div
        role="status"
        className="rounded-lg px-4 py-4 text-center"
        style={{ background: dt.blueSoft, border: `1px solid ${dt.cardBorder}`, borderRadius: dt.radiusSm }}
      >
        <div className="text-lg font-medium" style={{ color: dt.textPrimary }}>
          {translate("inviteOnly.successTitle")}
        </div>
        <div className="mt-2 text-sm" style={{ color: dt.textSecondary }}>
          {translate("inviteOnly.successBody")}
        </div>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      {errorKey && (
        <div
          role="alert"
          className="rounded-lg px-4 py-3 text-sm"
          style={{ background: dt.blueSoft, border: `1px solid ${dt.cardBorder}`, borderRadius: dt.radiusSm, color: dt.textPrimary }}
        >
          {translate(`inviteOnly.${errorKey}`)}
        </div>
      )}

      <Field
        id="bookDemoName"
        label={translate("inviteOnly.name")}
        value={name}
        onChange={setName}
        maxLength={MAX.name}
        invalid={showValidation && name.trim().length === 0}
      />

      <Field
        id="bookDemoEmail"
        label={translate("inviteOnly.email")}
        value={email}
        onChange={setEmail}
        maxLength={MAX.email}
        type="email"
        invalid={showValidation && !emailValid}
      />

      <Field
        id="bookDemoCompany"
        label={translate("inviteOnly.company")}
        value={company}
        onChange={setCompany}
        maxLength={MAX.company}
        invalid={showValidation && company.trim().length === 0}
      />

      <Field
        id="bookDemoPhone"
        label={translate("inviteOnly.phone")}
        value={phone}
        onChange={setPhone}
        maxLength={MAX.phone}
      />

      <Field
        id="bookDemoMessage"
        label={translate("inviteOnly.message")}
        value={message}
        onChange={setMessage}
        maxLength={MAX.message}
        multiline
      />

      {/* Honeypot: off-screen, not focusable, hidden from assistive tech. A human never fills it. */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-2 w-full rounded-lg px-4 py-3 text-base font-medium text-white transition-opacity disabled:opacity-60"
        style={{ background: dt.blue, borderRadius: dt.radiusSm }}
      >
        {translate("inviteOnly.submit")}
      </button>
    </form>
  );
};

/** Reads the JSON body defensively — an HTML body (SPA catch-all) must NOT count as success. */
const readOk = async (response: Response): Promise<boolean> => {
  try {
    const body = await response.json();
    return body?.ok === true;
  } catch {
    return false;
  }
};

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  type?: string;
  multiline?: boolean;
  invalid?: boolean;
}

const Field = ({ id, label, value, onChange, maxLength, type = "text", multiline, invalid }: FieldProps) => {
  const commonProps = {
    id,
    value,
    maxLength,
    "aria-invalid": invalid || undefined,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(e.target.value),
    className: "w-full rounded-lg px-3 py-2 text-base outline-none",
    style: {
      background: dt.inputBg,
      border: `1px solid ${invalid ? dt.amber : dt.cardBorder}`,
      borderRadius: dt.radiusSm,
      color: dt.textPrimary,
    },
  };

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm" style={{ color: dt.textSecondary }}>
        {label}
      </label>
      {multiline ? <textarea rows={4} {...commonProps} /> : <input type={type} {...commonProps} />}
    </div>
  );
};

export default BookDemoForm;
